import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { spore } from '@/spore/runtime';
import { bindSimulatorKeys } from '@/spore/simulator';
import { useSporeSnapshot } from '@/hooks/useSporeSnapshot';
import { DevicePanel, StatusBadge } from '@/components/lab/DevicePanel';
import { Oscilloscope } from '@/components/lab/Oscilloscope';
import { Meters } from '@/components/lab/Meters';
import { Gestures } from '@/components/lab/Gestures';
import { SimulatorPanel } from '@/components/lab/SimulatorPanel';
import { CalibrationPanel } from '@/components/lab/CalibrationPanel';
import { VoicePanel } from '@/components/lab/VoicePanel';
import { EventLog } from '@/components/lab/EventLog';
import { ChannelsPanel } from '@/components/lab/ChannelsPanel';
import { RecorderPanel, ProfessorVoicePanel } from '@/components/lab/Phase2Panels';
import { RecordingsPanel, VoiceAuditionPanel, SfxBankPanel } from '@/components/lab/Phase3Panels';
import { VoiceCheckPanel } from '@/components/lab/VoiceCheckPanel';
import { OfflinePanel } from '@/components/lab/OfflinePanel';
import { BinaryTouchPanel } from '@/components/lab/BinaryTouchPanel';
import { TrainingVideoPanel } from '@/components/lab/TrainingVideoPanel';
import { SporeSoundsPanel } from '@/components/lab/SporeSoundsPanel';
import { CreditLogPanel } from '@/components/lab/CreditLogPanel';
import { TouchTestOverlay, runLabTouchTest } from '@/components/lab/TouchTestOverlay';

export default function Lab() {
  const snap = useSporeSnapshot(20);
  useEffect(() => bindSimulatorKeys(spore.sim), []);
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target && e.target.tagName;
      if (e.repeat || e.ctrlKey || e.metaKey || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if (e.key.toLowerCase() === 'c') runLabTouchTest();
      if (e.key.toLowerCase() === 'b') spore.relearnBaseline();
      if (e.key.toLowerCase() === 'x') spore.saveSnapshot();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <main data-testid="signal-lab" className="lab-bg grain">
      <div className="relative z-10 mx-auto max-w-[1800px] px-6 py-6 lg:px-10">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b border-[var(--hair)] pb-5">
          <div>
            <div data-testid="specimen-label" className="label-eyebrow mb-2">SPORE LAB · HIVE MONITOR · [ SPECIMEN: THE SPORE<span className="normal-case">s</span> ] · BIOTRON CH 1–16</div>
            <h1 className="font-display text-5xl font-black sm:text-6xl">
              <span className="hive-stripes">Signal Lab</span> <span className="glow-spore">/ the SPORE<span className="normal-case">s</span></span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {snap.sim.enabled && <span data-testid="simulator-active-badge" className="border border-[var(--amber)] px-3 py-1 text-xs tracking-[0.2em] text-[var(--amber)]">SIMULATOR ACTIVE</span>}
            <StatusBadge status={snap.status.status} />
            <Link to="/" data-testid="nav-stage-link" className="btn-lab">Stage</Link>
          </div>
        </header>

        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-12"><ChannelsPanel channels={snap.channels} /></div>
          <div className="col-span-12"><BinaryTouchPanel snap={snap} /></div>
          <div className="col-span-12 lg:col-span-3"><DevicePanel info={snap.status} /></div>
          <div className="col-span-12 lg:col-span-9"><Oscilloscope snap={snap} /></div>
          <div className="col-span-12 md:col-span-6 lg:col-span-4"><Meters state={snap.state} stillThresholds={snap.thresholds.stillThresholdsS} /></div>
          <div className="col-span-12 md:col-span-6 lg:col-span-4"><Gestures counts={snap.counts} lastGesture={snap.lastGesture} gestures={snap.gestures} /></div>
          <div className="col-span-12 lg:col-span-4"><SimulatorPanel sim={snap.sim} /></div>
          <div className="col-span-12 lg:col-span-5"><CalibrationPanel snap={snap} /></div>
          <div className="col-span-12 lg:col-span-7"><VoicePanel gestures={snap.gestures} /></div>
          <div className="col-span-12"><EventLog events={snap.events} /></div>
          <div className="col-span-12 lg:col-span-7"><RecorderPanel /></div>
          <div className="col-span-12 lg:col-span-5"><ProfessorVoicePanel /></div>
          <div className="col-span-12 lg:col-span-6"><VoiceAuditionPanel /></div>
          <div className="col-span-12 lg:col-span-6"><RecordingsPanel /></div>
          <div className="col-span-12"><OfflinePanel /></div>
          <div className="col-span-12"><VoiceCheckPanel /></div>
          <div className="col-span-12"><SfxBankPanel /></div>
          <div className="col-span-12"><TrainingVideoPanel /></div>
          <div className="col-span-12"><SporeSoundsPanel /></div>
          <div className="col-span-12"><CreditLogPanel /></div>
        </div>
        <footer className="mt-6 text-xs text-[var(--dim)]">window.__spore exposes status · state · touching · touchMs · lastSymbol · detector · touchTest (read-only)</footer>
      </div>
      <TouchTestOverlay test={snap.touchTest} touching={snap.touch5.touching} hovering={snap.touch5.hovering} />
    </main>
  );
}
